'use client'

import { useId } from 'react'

// =============================================================
// Sparkline — pure SVG mini chart for table rows
// No chart library dependency. Renders 7d price trend.
// =============================================================

interface SparklineProps {
  data: number[]
  width?: number
  height?: number
}

export function Sparkline({ data, width = 96, height = 32 }: SparklineProps) {
  const id = useId()

  if (!data?.length) return null

  const positive = data[data.length - 1] >= data[0]
  const color = positive ? '#22c55e' : '#ef4444'
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const step = width / (data.length - 1)

  const points = data
    .map(
      (v, i) =>
        `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`,
    )
    .join(' ')

  const areaCoords = data.map((v, i) => {
    const x = i * step
    const y = height - ((v - min) / range) * (height - 4) - 2
    return `${x},${y}`
  })

  const lastX = (data.length - 1) * step
  const areaPath = `M0,${height} L${areaCoords.join(' L')} L${lastX},${height} Z`

  const gradientId = `sp-g${id.replace(/:/g, '')}`

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="flex-shrink-0"
      role="img"
      aria-label={`7 day price trend: ${positive ? 'up' : 'down'}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.12" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}